import {
  Dispatch,
  PropsWithChildren,
  RefObject,
  SetStateAction,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { useOnClickOutside } from "../../hooks";
import { DesignSystemProvider } from "../design-system-provider/design-system-provider";
import { dropdownBehaviourContentClass } from "./dropdown-behaviour.css";

export enum DropdownBehaviourHorizontalPositionEnum {
  Left = "Left",
  Right = "Right",
  Center = "Center",
}

export enum DropdownBehaviourVerticalPositionEnum {
  Top = "Top",
  Bottom = "Bottom",
}

type DropdownBehaviourProps = PropsWithChildren & {
  /**
   * Horizontal position of Dropdown
   */
  horizontalPosition?: DropdownBehaviourHorizontalPositionEnum;
  /**
   * Vertical position of Dropdown
   */
  verticalPosition?: DropdownBehaviourVerticalPositionEnum;
  /**
   * Is the Dropdown open/visible
   */
  isOpen: boolean;
  /**
   * Action to change open status
   */
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  /**
   * Is dropDown would have flex vertical position
   */
  isVerticalPositionFlexible?: boolean;
};

export const DropdownBehaviour = forwardRef<
  HTMLInputElement,
  DropdownBehaviourProps
>(
  (
    {
      children,
      isOpen,
      setIsOpen,
      verticalPosition = DropdownBehaviourVerticalPositionEnum.Bottom,
      horizontalPosition = DropdownBehaviourHorizontalPositionEnum.Center,
      isVerticalPositionFlexible,
    }: DropdownBehaviourProps,
    ref
  ) => {
    let portalContainer = document.getElementById("dropdown-container");
    if (!portalContainer) {
      const portalElementContainer = document.createElement("div");
      portalElementContainer.setAttribute("id", "dropdown-container");
      document.body.appendChild(portalElementContainer);
      portalContainer = portalElementContainer;
    }

    const dropdownBehaviourContent = useRef<HTMLDivElement>(null);

    const [verticalPositionStyles, setVerticalPositionStyles] = useState({});
    const [horizontalPositionStyles, setHorizontalPositionStyles] = useState(
      {}
    );

    useOnClickOutside(
      dropdownBehaviourContent,
      hideDropdown,
      ref as RefObject<HTMLInputElement>
    );

    function hideDropdown() {
      setIsOpen(false);
    }

    function placeDropdown() {
      const dimensions = (
        ref as RefObject<HTMLInputElement>
      )?.current?.getBoundingClientRect();
      let currenVerticalPosition = verticalPosition;

      if (!dimensions) return;

      // save all used values to variables
      const scrollPosition = window.scrollY;
      const pageHeight = document.body.scrollHeight;
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const topPosition = dimensions.top;
      const rightPosition = dimensions.right;
      const leftPosition = dimensions.left;
      const bottomPosition = dimensions.bottom;
      const elementWidth = dimensions.width;
      const dropdownContentHeight =
        dropdownBehaviourContent.current?.getBoundingClientRect().height;

      // Change vertical position depends on free space and if flag is enabled
      if (isVerticalPositionFlexible) {
        if (dropdownContentHeight && topPosition < dropdownContentHeight) {
          currenVerticalPosition = DropdownBehaviourVerticalPositionEnum.Bottom;
        } else if (
          dropdownContentHeight &&
          viewportHeight - bottomPosition < dropdownContentHeight
        ) {
          currenVerticalPosition = DropdownBehaviourVerticalPositionEnum.Top;
        }
      }

      // Set vertical styles
      if (
        currenVerticalPosition === DropdownBehaviourVerticalPositionEnum.Top
      ) {
        setVerticalPositionStyles({
          bottom: `${pageHeight - scrollPosition - topPosition}px`,
        });
      } else if (
        currenVerticalPosition === DropdownBehaviourVerticalPositionEnum.Bottom
      ) {
        setVerticalPositionStyles({
          top: `${scrollPosition + bottomPosition}px`,
        });
      }

      // Set horizontal position
      if (horizontalPosition === DropdownBehaviourHorizontalPositionEnum.Left) {
        setHorizontalPositionStyles({
          left: `${leftPosition}px`,
        });
      } else if (
        horizontalPosition === DropdownBehaviourHorizontalPositionEnum.Right
      ) {
        setHorizontalPositionStyles({
          right: `${viewportWidth - rightPosition}px`,
        });
      } else {
        setHorizontalPositionStyles({
          left: `${leftPosition}px`,
          width: `${elementWidth}px`,
        });
      }
    }

    useEffect(() => {
      if (isOpen) {
        placeDropdown();
        // We need this style to position our dropdown in a correct place on page
        document.body.style.position = "relative";
      } else {
        document.body.style.position = "";
      }
    }, [isOpen, document.body.scrollHeight]);

    if (!isOpen) return null;

    return (
      <>
        {createPortal(
          <DesignSystemProvider>
            <div
              ref={dropdownBehaviourContent}
              style={{ ...verticalPositionStyles, ...horizontalPositionStyles }}
              className={dropdownBehaviourContentClass}
            >
              {children}
            </div>
          </DesignSystemProvider>,
          portalContainer
        )}
      </>
    );
  }
);

DropdownBehaviour.displayName = "DropdownBehaviour";
